/**
 * KnowledgeCenterLayout — standalone chrome for the Knowledge Center routes
 * (/help, /help/:articleId), which open in a NEW TAB from the support widget.
 *
 * Deliberately renders WITHOUT the app Header/Sidebar: these are reading
 * pages, and the surrounding app chrome (nav, notifications, org switcher)
 * is noise in a documentation tab. KnowledgeTopBar + KnowledgeFooter keep
 * identity and a way back into the app. Auth is still required — the routes
 * are wrapped in ProtectedRoute in App.tsx (only the layout differs from
 * other pages).
 */

import { Outlet } from 'react-router-dom';
import styled from 'styled-components';
import { SupportWidget, SUPPORT_LAUNCHER_SIZE_PX } from '../SupportWidget';
import { ScrollToTop } from './ScrollToTop';
import { KnowledgeTopBar, KnowledgeFooter } from './chrome';

const Shell = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.backgrounds.page};
`;

/* Full-bleed canvas: pages own their sections (the index page paints a
   full-width hero band), so the layout imposes no width or padding here —
   each page centers and pads its own content.

   Deliberately a <div>, not <main>: every page below renders its own <main>,
   and nesting them is invalid HTML that gives screen readers two "main"
   landmarks (jump-to-main would land on this wrapper rather than the
   article). This element is purely a flex/padding shell. */
const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  /* Same clearance contract as MainLayout (#537): page-bottom content
     (footer links, the support band) must never sit under the launcher. */
  padding-bottom: ${({ theme }) =>
    `calc(${theme.spacing[6]} + ${SUPPORT_LAUNCHER_SIZE_PX}px + ${theme.spacing[4]})`};
`;

export function KnowledgeCenterLayout() {
  return (
    <Shell>
      <ScrollToTop />
      <KnowledgeTopBar />
      <Content>
        <Outlet />
      </Content>
      <KnowledgeFooter />
      {/* The docs escalation path: search failed → message support right
          here. Same widget instance pattern as MainLayout. */}
      <SupportWidget />
    </Shell>
  );
}

export default KnowledgeCenterLayout;
